/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { isStrEmptyOrNull } from '../../common/UtilityFunctions';
import { useLDCContext } from '../../contexts/LoanDefaultCodesContext';
import LoanInformationStyles from '../../css/LoanInformation.module.css';
import dateInputRow from './DateInputRow';
import SelectDropdown from './Dropdown';
import PatternInput from './PatternInput';
import TextInput from './TextInput';
import DDSCodeList from './List';

const mainLDCCol = () => {
  const {
    loanPropCode,
    LDCInfo,
    LDCFunctions,
    LDCUIStatuses,
    filteredDDSCodeOptions,
    filteredDDRCodeOptions,
    filteredServicingOccupancyOptions,
  } = useLDCContext();
  const { handleLDCChange, editDDSvars, checkReportingDate, allowOnlyCurrentPastDates } = LDCFunctions;
  const { isEditable } = LDCUIStatuses;

  const {
    DDRDate,
    ForeclosureSaleHeldDate,
    bkFileDate,
    dtOfBankruptcyDischarged,
    dtOfDismisOrMfrGrntdOrStayLifted,
    reaffirmationFiledDate,
    ServOccuStatusVerifiedDate,
    VacantDate,
    DDSReportingDate,
    DDSEventDate,
    priorBankruptcyFiledDate,
  } = LDCInfo.dates;

  const {
    foreclosureMainStatusOptions,
    deedInLieuStatusOptions,
    DBStatusOptions,
    RespPetitionPartyOptions,
    ShortSaleOptions,
    BankruptcyStatusOptions,
    BankruptcyChapterOptions,
    BankruptcyYesNoOptions,
    BankruptcyFiledByOptions,
  } = LDCInfo.QIDOptions;

  const {
    ddrReason,
    defaultBankruptcyStatus,
    foreclosureMainStatus,
    deedInLieuStatus,
    bkStatus,
    bkChapter,
    bkCaseNum,
    prePetitionPmntRespParty,
    postPetitionPmntRespParty,
    investorCode,
    investorCodeDescription,
    DDSCode,
    ddsCodesListForLoan,
    shortSaleStatus,
    servicingOccupancy,
    priorBankruptcy,
    bkFileBy,
  } = LDCInfo;

  return (
    <table className={LoanInformationStyles.loanInfoTableSection}>
      <tbody>
        <TextInput
          label={'Investor'}
          value={investorCode + ' - ' + investorCodeDescription}
          name={''}
          disabled={true}
        ></TextInput>

        <SelectDropdown
          name={'DDSCode'}
          label={'Add DDS Code'}
          value={DDSCode}
          options={filteredDDSCodeOptions}
          func={(e) => {
            handleLDCChange('edit', 'DDSCode', e.target.value);
            editDDSvars();
          }}
        ></SelectDropdown>

        {!isStrEmptyOrNull(DDSCode) &&
          dateInputRow('Reporting Date', DDSReportingDate, 'dates.DDSReportingDate', !isEditable, (e) => {
            checkReportingDate(e);
          })}

        {!isStrEmptyOrNull(DDSCode) && dateInputRow('DDS Date', DDSEventDate, 'dates.DDSEventDate', !isEditable)}

        {ddsCodesListForLoan.length !== 0 && <DDSCodeList list={ddsCodesListForLoan}></DDSCodeList>}

        <SelectDropdown
          name={'ddrReason'}
          label={'DDR Reason'}
          value={ddrReason}
          options={filteredDDRCodeOptions}
        ></SelectDropdown>

        {dateInputRow('DDR Date', DDRDate, 'dates.DDRDate', !isEditable)}

        <SelectDropdown
          name={'defaultBankruptcyStatus'}
          label={'DB Status'}
          value={defaultBankruptcyStatus}
          options={DBStatusOptions}
        ></SelectDropdown>

        <SelectDropdown
          name={'bkChapter'}
          label={'Bankruptcy Chapter'}
          value={bkChapter}
          options={BankruptcyChapterOptions}
        ></SelectDropdown>

        {dateInputRow('Bankruptcy File Date', bkFileDate, 'dates.bkFileDate', !isEditable)}

        <PatternInput
          label={'Bankruptcy Case No'}
          formatStr={'##-#####'}
          value={bkCaseNum}
          name={'bkCaseNum'}
          disabled={!isEditable}
          func={(e) => {
            handleLDCChange('edit', 'bkCaseNum', e.target.value.replace(/\D/g, ''));
          }}
        ></PatternInput>

        <SelectDropdown
          name={'bkStatus'}
          label={'Bankruptcy Status'}
          value={bkStatus}
          options={BankruptcyStatusOptions}
        ></SelectDropdown>

        <SelectDropdown
          name={'bkFileBy'}
          label={'Bankruptcy Filed By'}
          value={bkFileBy}
          options={BankruptcyFiledByOptions}
        ></SelectDropdown>

        <SelectDropdown
          name={'priorBankruptcy'}
          label={'Prior Bankruptcy'}
          value={priorBankruptcy}
          options={BankruptcyYesNoOptions}
        ></SelectDropdown>

        {priorBankruptcy == 'Y' &&
          dateInputRow(
            'Prior Bankruptcy File Date',
            priorBankruptcyFiledDate,
            'dates.priorBankruptcyFiledDate',
            !isEditable,
          )}

        <SelectDropdown
          name={''}
          label={'Short Sale Status'}
          value={shortSaleStatus}
          options={ShortSaleOptions}
          disable={true}
        ></SelectDropdown>

        <SelectDropdown
          name={''}
          label={'Pre-Petition Payments'}
          value={prePetitionPmntRespParty}
          options={RespPetitionPartyOptions}
          disable={true}
        ></SelectDropdown>

        <SelectDropdown
          name={''}
          label={'Post-Petition Payments'}
          value={postPetitionPmntRespParty}
          options={RespPetitionPartyOptions}
          disable={true}
        ></SelectDropdown>

        {dateInputRow('Reaffirm Date', reaffirmationFiledDate, '', true)}

        {bkStatus === '3' && dateInputRow('Bankruptcy Discharge Date', dtOfBankruptcyDischarged, '', true)}

        {(bkStatus === '2' || bkStatus === '4') &&
          dateInputRow(
            'Bankruptcy Date of Dismissal/MFR Granted/Stay Lifted',
            dtOfDismisOrMfrGrntdOrStayLifted,
            '',
            true,
          )}

        <SelectDropdown
          name={'foreclosureMainStatus'}
          label={'Foreclosure Flag'}
          value={foreclosureMainStatus}
          options={foreclosureMainStatusOptions}
        ></SelectDropdown>

        {dateInputRow('FC Termination Date', ForeclosureSaleHeldDate, 'dates.ForeclosureSaleHeldDate', !isEditable)}

        <SelectDropdown
          name={'deedInLieuStatus'}
          label={'Deed In Lieu Flag'}
          value={deedInLieuStatus}
          options={deedInLieuStatusOptions}
        ></SelectDropdown>

        <SelectDropdown
          name={'servicingOccupancy'}
          label={'Servicing Occupancy'}
          value={servicingOccupancy}
          options={filteredServicingOccupancyOptions}
          func={(e) => {
            handleLDCChange('edit', 'servicingOccupancy', e.target.value);
            handleLDCChange('edit', 'propCode', loanPropCode);
          }}
        ></SelectDropdown>

        {dateInputRow(
          'Servicing Occupancy Status Verified Date',
          ServOccuStatusVerifiedDate.activeDate,
          'dates.ServOccuStatusVerifiedDate.activeDate',
          true,
        )}

        {(servicingOccupancy === '01' || servicingOccupancy === '3' || servicingOccupancy === '07') &&
          dateInputRow('Vacant Date', VacantDate, 'dates.VacantDate', !isEditable, allowOnlyCurrentPastDates)}
      </tbody>
    </table>
  );
};

export default mainLDCCol;
